import {
  action,
  makeObservable,
  observable,
  onBecomeObserved,
  override,
} from "mobx"

import { filterFields } from "@src/pages/transactionsIn/constants"
import { catchErrors, chargeApi, paymentsApi } from "@src/shared/api"
import { ChargeResponse, Pagination, Payment } from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { wrapSingleton } from "@src/shared/classes/utils/wrapSingleton"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"

export type ChargesLoadRequest = {
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  externalId?: string
  page?: number
  projectId?: string
  status?:
    | "canceled"
    | "completed"
    | "expired"
    | "new"
    | "pending"
    | "resolved"
    | "resolving"
    | "unresolved"
    | "without-failed"
}

export type ChargeCollectionFilter = {
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  status: string
}

export class ChargesCollection extends loading(
  ["fetch", "loadPaymentById"],
  CollectionWithPagination<ChargeResponse>,
) {
  filter = new CollectionFilters<ChargeCollectionFilter>(
    filterFields,
    this.load,
  )

  paymentById: Record<string, Payment> = {}

  constructor() {
    super()

    makeObservable(this, {
      fetch: action.bound,
      findPaymentById: action.bound,
      loadPaymentById: action.bound,
      paymentById: observable.shallow,
    })

    onBecomeObserved(this, "isLoading", this.load)
  }

  private createRequest(req: ChargesLoadRequest) {
    const { page = 0, status = "without-failed" } = req

    return [
      this.order,
      this.calcOffset(page),
      this.pageSize,
      req.projectId,
      req.externalId,
      req.date?.dateFrom,
      req.date?.dateTo,
      status as any,
    ]
  }

  async fetch(req: ChargesLoadRequest = {}) {
    try {
      const {
        data: { data },
      } = await chargeApi.getCharges(...(this.createRequest(req) as any))

      if (data) {
        return {
          data: data.data as ChargeResponse[],
          pagination: data.pagination as Pagination,
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }

    return null
  }

  findPaymentById(paymentId: string) {
    const paymentFromCharges = this.items
      .flatMap(({ payments = [] }) => payments)
      .find(({ id }) => id === paymentId)

    return paymentFromCharges ?? this.paymentById[paymentId]
  }

  async loadPaymentById(paymentId: string) {
    if (this.findPaymentById(paymentId)) return

    try {
      const {
        data: { data },
      } = await paymentsApi.getPayments(undefined, 0, 1, paymentId)

      const payment = data?.data?.find(({ id }) => id === paymentId)

      if (payment) {
        this.paymentById[paymentId] = payment
      }
    } catch (e: any) {
      catchErrors(e)
    }
  }
}

export const GlobalChargesCollection = wrapSingleton(ChargesCollection)
